import React from "react";
import styled from "styled-components";
import Colors from "../../constants/Colors";
import {deviceMax, deviceMin} from "../../../styles/device";
import {useAmp} from "next/amp";
import Image from "next/image";

export const config = {amp: 'hybrid'}

declare global {
    namespace JSX {
        interface IntrinsicElements {
            [elemName: string]: any;
        }
    }
}

type IProps = {
    icon: string
    title: string
    content: string
};

const AdvertisementPropertyLines = ({icon, title, content}: IProps) => {

    const isAmp = useAmp();

    return (
        <AdvertisementPropertyLinesWrapper>
            <div key={Math.random()}
                 className={"property-lines-container"}>
                <div className="property-lines-row">
                    <div className="property-lines-wrapper">
                        <div
                            className={'property-lines-icon property-lines-icon__about_us'}>
                            {isAmp ? (
                                <amp-img
                                    src={`${icon}`}
                                    alt={title}
                                    width={168}
                                    height={126}
                                    layout={'fixed'}
                                />
                            ) : (
                                <Image loading={'lazy'} width={168}
                                       height={126}
                                       sizes={'100vw'}
                                       style={{width: 168, height: 126}}
                                       src={`${icon}`}
                                       alt={title} title={title}
                                       className={'image'}
                                />
                            )}

                        </div>
                    </div>
                    <div
                        className={'property-lines-wrapper'}>
                        <h2>
                            {title}
                        </h2>
                        <p>
                            {content}
                        </p>
                    </div>
                </div>
            </div>

        </AdvertisementPropertyLinesWrapper>
    )
}

const AdvertisementPropertyLinesWrapper = styled.div`
  position: relative;
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 30px;
  background: ${Colors.white};
  border-radius: 6px;

  @media ${deviceMax.tablet} {
    padding: 0 0 30px;
    background: unset;

    &:first-child {
      padding-top: 70px;
    }
  }

  > .about-us-title {
    font-weight: 800;
    font-size: 18px;
    line-height: 55px;
    letter-spacing: 0;
    text-transform: uppercase;
    color: ${Colors.brandColor};
    margin-left: 56px;
    padding-top: 23px;
    box-sizing: border-box;
    margin-top: 0;

    @media ${deviceMax.tablet} {
      margin: 0 auto 21px;
      line-height: 30px;
      letter-spacing: 0;
      color: ${Colors.black};
    }
  }

  > .property-lines-container {
    position: relative;
    display: flex;
    flex-direction: row;
    max-width: 1200px;
    margin: 0 auto 18px;
    border-radius: 10px;
    padding: 0 89px 5px 15px;

    &:nth-child(2) {
      padding-top: 20px;
    }

    &.basin-container {
      border: none;
      border-radius: unset;
      max-width: 1100px;
      margin-bottom: 0;
      padding: 35px 45px 31px 54px;
      box-shadow: unset;

      > .property-lines-row {
        border-bottom: 1px solid ${Colors.softGreyColor};
        padding-bottom: 30px;
      }

      &:last-child {
        padding-bottom: 35px;

        > .property-lines-row {
          border-bottom: none;
          margin-bottom: -30px;
        }
      }

      @media ${deviceMax.tablet} {
        padding: 0 24px 20px;
        margin-left: -44px;
        margin-right: -44px;

        &:first-child {
          padding-top: 0;
        }
      }
    }

    @media ${deviceMax.tablet} {
      flex-direction: column;
      padding: 6px 20px 30px;
      align-items: center;
      justify-content: center;
      background: ${Colors.white};

      &:last-child {
        margin-bottom: 0;
      }
    }

    > .property-lines-row {
      position: relative;
      display: flex;
      flex-direction: row;

      @media ${deviceMax.tablet} {
        flex-direction: column;
        align-items: center;
        justify-content: center;
        &:last-child {
          margin-bottom: 0;
        }
      }

      > .property-lines-wrapper {

        &:first-child {
          flex: 1;
        }

        &:last-child {
          flex: 6;

          @media ${deviceMin.tablet} {
            padding-top: 20px;
          }
        }

        &.basin-wrapper {
          margin-left: 28px;

          @media ${deviceMax.tablet} {
            margin-left: 0;
          }

          @media ${deviceMin.tablet} {
            padding-top: 0;
          }
        }

        > .property-lines-icon {
          position: relative;
          width: 100%;
          height: 100%;

          @media ${deviceMax.tablet} {
            margin-top: 30px;
            margin-bottom: 20px;
          }

          &__about_us {
            width: 168px;
          }

          > .image {
            width: 100%;
            height: auto;

            &__basin {
              max-width: 106px;
            }
          }
        }

        > h2 {
          font-family: 'Manrope', sans-serif;
          font-weight: 600;
          font-size: 24px;
          line-height: 32px;
          letter-spacing: 0;
          color: ${Colors.blackColor};
          margin-bottom: 28px;
          margin-top: 0;

          @media ${deviceMax.tablet} {
            text-align: center;
            width: 100%;
            font-size: 16px;
            font-weight: 800;
          }
        }

        > .basin {

          &_date {
            font-family: 'Manrope', sans-serif;
            font-weight: 500;
            font-size: 14px;
            line-height: 30px;
            letter-spacing: 0;
            color: ${Colors.purpleColor};
            margin-bottom: 20px;

            @media ${deviceMax.tablet} {
              margin: 0 auto 20px;
            }
          }

          &_text {
            font-family: 'Manrope', sans-serif;
            font-weight: 500;
            font-size: 18px;
            line-height: 30px;
            letter-spacing: 0;
            color: ${Colors.blackColor};
            margin-bottom: 20px;

            @media ${deviceMax.tablet} {
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
            }
          }

          &_link {
            font-family: 'Manrope', sans-serif;
            font-weight: 500;
            font-size: 18px;
            line-height: 30px;
            letter-spacing: 0;
            color: ${Colors.blackColor};
            text-decoration: underline;
            transition: all .3s ease;

            &:hover {
              color: ${Colors.brandColor};
            }

            @media ${deviceMax.tablet} {
              display: inline-block;
              width: 100%;
              text-align: center;
              font-weight: 800;
              font-size: 14px;
            }
          }
        }

        > p {
          font-family: 'Manrope', sans-serif;
          font-weight: 400;
          font-size: 16px;
          line-height: 25px;
          letter-spacing: 0;
          color: ${Colors.landingGreyColor};
          margin: 0;
          white-space: pre-line;

          @media ${deviceMax.tablet} {
            text-align: center;
          }
        }
      }
    }
  }
`;

export default AdvertisementPropertyLines;
